import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../core/services/language.service';
import { IToolbar } from '@tecalisdev/infinity';
import { setFirstHourDay, setLastHourDay } from '../../core/utils/utils';
import { createDateRangeFilters } from '../../shared/list/create-date-range-filters.util';
import { MasterService } from '../../shared/services/master.service';
import { RolePermissionsService } from '../../core/services/role-permissions.service';

@Injectable({
	providedIn: 'root',
})
export class PanelHelperService {
	private trans = inject(TranslateService);
	private masterService = inject(MasterService);
	private langSvc = inject(LanguageService);
	private rolePermissions = inject(RolePermissionsService);

	public currentFilters = signal<any>(null);

	private get periodOptions() {
		return [
			{ label: this.trans.instant('FILTERS.TODAY'), value: 'today', id: 1 },
			{ label: this.trans.instant('FILTERS.YESTERDAY'), value: 'yesterday', id: 2 },
			{ label: this.trans.instant('FILTERS.LAST_7_DAYS'), value: 'last_7_days', id: 3 },
			{ label: this.trans.instant('FILTERS.LAST_30_DAYS'), value: 'last_30_days', id: 4 },
			{ label: this.trans.instant('FILTERS.LAST_6_MONTHS'), value: 'last_6_months', id: 5 },
		];
	}

	readonly toolbar: Signal<IToolbar> = computed<IToolbar>(() => {
		this.langSvc.currentLang(); // dependencia reactiva: recalcula al cambiar idioma
		const filters: IToolbar['filters'] = [
			{
				type: 'select',
				name: 'period',
				title: this.trans.instant('FILTERS.PERIOD'),
				placeholder: this.trans.instant('FILTERS.SELECT_PERIOD'),
				value: this.periodOptions[0],
				options: this.periodOptions,
			},
			...createDateRangeFilters({
				fromTitle: this.trans.instant('FILTERS.FROM'),
				toTitle: this.trans.instant('FILTERS.TO'),
			}).map((f) => ({ ...f, datepickerType: 'date-time' as any })),
		];

		// Si hay más de una compañía, agregar filtro de compañía
		if (this.masterService.companies.length > 1 || this.rolePermissions.isSuperadmin()) {
			filters.unshift({
				type: 'select',
				title: this.trans.instant('FILTERS.COMPANY'),
				name: 'company_uuid',
				placeholder: this.trans.instant('FILTERS.COMPANY'),
				options: this.masterService.companies,
				search: true,
			});
		}

		return {
			title: this.trans.instant('PANEL.TITLE'),
			filters,
		};
	});

	public updateFilters(newFilters: any): void {
		const current = this.currentFilters();
		const updated: any = {};

		// Si viene un period, calcular fechas desde el period
		if (newFilters.period) {
			const dates = this.calculateDates(newFilters.period.value);
			updated.start_at = dates.start_at;
			updated.end_at = dates.end_at;
			updated.periodId = newFilters.period.id;
		} else if (
			'period' in newFilters &&
			!newFilters.period &&
			!this.hasIncomingManualDates(newFilters)
		) {
			// Se deseleccionó el period sin rango manual en este evento: vaciar fechas
			updated.start_at = undefined;
			updated.end_at = undefined;
		} else {
			// Fechas manuales o merge parcial (p. ej. solo company_uuid)
			updated.start_at = newFilters.start_at || current?.start_at || undefined;
			updated.end_at = newFilters.end_at || current?.end_at || undefined;
			if (this.hasIncomingManualDates(newFilters)) {
				updated.periodId = undefined;
			} else {
				updated.periodId = current?.periodId;
			}
		}

		// Copiar otros filtros (company_uuid, etc.)
		for (const key of Object.keys(newFilters)) {
			if (!['period', 'start_at', 'end_at'].includes(key) && newFilters[key] != null) {
				updated[key] = newFilters[key];
			}
		}

		this.currentFilters.set(updated);
	}

	/** Hay intención de filtrar por Desde/Hasta en el payload (p. ej. al pasar de periodo a fechas manuales). */
	private hasIncomingManualDates(f: Record<string, unknown>): boolean {
		const s = f['start_at'];
		const e = f['end_at'];
		if (s != null && s !== '') {
			return true;
		}
		if (e != null && e !== '') {
			return true;
		}
		return false;
	}

	private calculateDates(period: string): { start_at: string; end_at: string } {
		const now = new Date();
		switch (period) {
			case 'today':
				return this.getTodayRange(now);
			case 'yesterday':
				return this.getYesterdayRange(now);
			case 'last_7_days':
				return this.getLastNDaysRange(now, 7);
			case 'last_30_days':
				return this.getLastNDaysRange(now, 30);
			case 'last_6_months':
				return this.getLastNMonthsRange(now, 6);
		}
		return this.getTodayRange(now);
	}

	/**
	 * NIF-982: mismos helpers que el listado (`setFirstHourDay` / `setLastHourDay`).
	 * Día calendario del navegador → ISO UTC; el back no redefine “hoy”.
	 */
	private getYesterdayRange(now: Date): { start_at: string; end_at: string } {
		const day = new Date(now);
		day.setDate(day.getDate() - 1);
		return { start_at: setFirstHourDay(day), end_at: setLastHourDay(day) };
	}

	private getTodayRange(now: Date): { start_at: string; end_at: string } {
		return { start_at: setFirstHourDay(now), end_at: setLastHourDay(now) };
	}

	private getLastNDaysRange(now: Date, n: number): { start_at: string; end_at: string } {
		const start = new Date(now);
		start.setDate(now.getDate() - (n - 1));
		return { start_at: setFirstHourDay(start), end_at: setLastHourDay(now) };
	}

	private getLastNMonthsRange(now: Date, n: number): { start_at: string; end_at: string } {
		const start = new Date(now);
		start.setMonth(now.getMonth() - (n - 1));
		return { start_at: setFirstHourDay(start), end_at: setLastHourDay(now) };
	}
}
